import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import axios from 'axios';

const initialState = {
  basicInfo: {},
  businessLocation: {},
  registrationAndCompliance: {
    complianceInfo: {},
    file: null,
  },
  servicesAndCapabilities: {},
  diversityData: [],
  testimonialsAndReferences: [],
  membershipsCertifications: {
    memberships: [],
    certifications: [],
  },
  loading: false,
  error: null,
  data: null,
  userId: null,
  supplierId: null,
  accountType: ''
};
const fetchWithAuth = async (url, method = "GET", body = null) => {
  const token = Cookies.get("authToken");

  if (!token) {
    throw new Error("Token is missing");
  }

  const isFormData = body instanceof FormData;

  const headers = {
    Authorization: `Bearer ${token}`,
  };


  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(url, {
    method,
    headers,
    body: isFormData ? body : body ? JSON.stringify(body) : null,
  });

  if (response.status === 401) {
    Cookies.remove("authToken");
    throw new Error("Token is invalid or expired. Please log in again.");
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Request failed");
  }

  return response.json();
};

export const postBasicInfo = createAsyncThunk(
  "formData/postBasicInfo",
  async (basicInfo, { rejectWithValue }) => {
    try {
      const data = await fetchWithAuth(
        "https://sourceiq-login-dha8dahravh6eyet.eastus-01.azurewebsites.net/api/supplier/basic-info",
        "POST",
        basicInfo
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateLocationInfo = createAsyncThunk(
  "formData/updateLocationInfo",
  async ({ id, locationInfo }, { rejectWithValue }) => {
    try {
      const data = await fetchWithAuth(
        "https://sourceiq-login-dha8dahravh6eyet.eastus-01.azurewebsites.net/api/supplier/address",
        "PUT",
        { id, locationInfo }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateServicesAndCapabilities = createAsyncThunk(
  "formData/updateServicesAndCapabilities",
  async ({ id, serviceInfo }, { rejectWithValue }) => {
    try {
      const data = await fetchWithAuth(
        "https://sourceiq-login-dha8dahravh6eyet.eastus-01.azurewebsites.net/api/supplier/services-capabilities",
        "PUT",
        { id, serviceInfo }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateDiversityCertifications = createAsyncThunk(
  "formData/updateDiversityCertifications",
  async ({ id, diversityInfo }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append(
        "data",
        JSON.stringify(diversityInfo.map((info) => ({
          id,
          certName: info.certName,
          certId: info.certId,
          agency: info.agency,
        })))
      );

      diversityInfo.forEach((info, index) => {
        if (info.file) {
          formData.append("files", info.file);
        }
      });

      const response = await fetchWithAuth(
        "https://sourceiq-login-dha8dahravh6eyet.eastus-01.azurewebsites.net/api/supplier/diversity",
        "PUT",
        formData,
        true 
      );

      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const submitEndorsement = createAsyncThunk(
  "formData/submitEndorsement",
  async (endorsements, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      const endorsementData = endorsements.map((endorsement) => ({
        id: endorsement.id,
        reference: endorsement.reference,
        endorsementInfo: endorsement.endorsementInfo,
      }));

      formData.append("data", JSON.stringify(endorsementData));

      endorsements.forEach((endorsement, index) => {
        if (endorsement.file) {
          formData.append("file", endorsement.file, endorsement.originalName);
        }
      });

      const response = await fetchWithAuth(
        "https://sourceiq-login-dha8dahravh6eyet.eastus-01.azurewebsites.net/api/supplier/endorsement",
        "PUT",
        formData
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit endorsement");
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const submitDiversity = createAsyncThunk(
  "formData/submitDiversity",
  async (diversityData, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      diversityData.diversityFiles.forEach((certificate) => {
        formData.append(
          "data",
          JSON.stringify({
            id: diversityData.userId,
            certId: certificate.certId,
            agency: certificate.agency,
          })
        );

        if (certificate.file) {
          formData.append("files", certificate.file, certificate.file.name);
        }
      });

      const response = await fetchWithAuth(
        "https://sourceiq-login-dha8dahravh6eyet.eastus-01.azurewebsites.net/api/supplier/diversity", // Your backend endpoint
        "PUT",
        formData
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to submit diversity information"
        );
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const submitMembershipsCertifications = createAsyncThunk(
  "formData/submitMembershipsCertifications",
  async ({ memberInfo }, { rejectWithValue }) => {
    try {
      const data = await fetchWithAuth(
        "https://sourceiq-login-dha8dahravh6eyet.eastus-01.azurewebsites.net/api/supplier/memberships",
        "PUT",
        { ...memberInfo }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const submitComplianceInfo = createAsyncThunk(
  "formData/submitComplianceInfo",
  async ({ file, complianceInfo }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("data", JSON.stringify(complianceInfo));

      const response = await fetchWithAuth(
        "https://sourceiq-login-dha8dahravh6eyet.eastus-01.azurewebsites.net/api/supplier/compliance",
        "PUT",
        formData
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to submit compliance info"
        );
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const confirmSupplierRegistration = createAsyncThunk(
  'buyerFormData/confirmSupplierRegistration',
  async (userId, { rejectWithValue }) => {
    const token = Cookies.get('authToken');

    if (!token) {
      return rejectWithValue('Token is missing');
    }

    try {
      const response = await axios.post(
        'https://sourceiq-login-dha8dahravh6eyet.eastus-01.azurewebsites.net/api/supplier/confirm-registration',
        { userId }, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const formDataSlice = createSlice({
  name: "formData",
  initialState,
  reducers: {
    setBasicInfo: (state, action) => {
      state.basicInfo = action.payload;
    },
    setSupplierId: (state, action) => {
      state.supplierId = action.payload;
    },
    setAccountType: (state, action) => {
      state.accountType = action.payload;
    },
     setBusinessLocation: (state, action) => {
      state.businessLocation = action.payload;
    },
    setServicesAndCapabilities: (state, action) => {
      state.servicesAndCapabilities = action.payload;
    },
    setComplianceInfo: (state, action) => {
      state.registrationAndCompliance.complianceInfo =
        action.payload.complianceInfo;
      state.registrationAndCompliance.file = action.payload.file;
    },
    setEndorstmentInfo: (state, action) => {
      state.testimonialsAndReferences = action.payload;
    },
    setDiversityInfo: (state, action) => {
      state.diversityData = action.payload;
    },
    setMembershipsCertifications: (state, action) => {
      const { memberships, certifications } = action.payload;
      state.membershipsCertifications = {
        memberships,
        certifications,
      };
    },
    updateFormData: (state, action) => {
      state.data = action.payload;
    },
    resetFormData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(postBasicInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postBasicInfo.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.data = payload;
        state.supplierId = payload.supplier._id;
        state.accountType = payload.supplier.accountType;
      })
      .addCase(postBasicInfo.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(updateLocationInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLocationInfo.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.data = payload;
      })
      .addCase(updateLocationInfo.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(updateServicesAndCapabilities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateServicesAndCapabilities.fulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.data = payload;
        }
      )
      .addCase(updateServicesAndCapabilities.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(submitEndorsement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitEndorsement.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.data = payload;
      })
      .addCase(submitEndorsement.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });
    builder
      .addCase(submitMembershipsCertifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        submitMembershipsCertifications.fulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.data = payload;
        }
      )
      .addCase(
        submitMembershipsCertifications.rejected,
        (state, { payload }) => {
          state.loading = false;
          state.error = payload;
        }
      );
    builder
      .addCase(submitComplianceInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitComplianceInfo.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.data = payload;
      })
      .addCase(submitComplianceInfo.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });
  },
});

export const {
  setBasicInfo,
  setBusinessLocation,
  setComplianceInfo,
  setEndorstmentInfo,
  setDiversityInfo,
  setServicesAndCapabilities,
  resetFormData,
  setMembershipsCertifications,
  updateFormData,
  setSupplierId,
  setAccountType
} = formDataSlice.actions;
export default formDataSlice.reducer;